import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  Save as SaveIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import api from '../config/api';
import useStore from "../config/store";
import { useTranslation } from "react-i18next";

const emptyCompanyContext = {
  companyDescription: "",
  teamDescription: "",
  culture: "",
  values: "",
  leadershipStyle: "",
  workStyle: "",
  expectations: "",
  additionalContext: "",
};

const companyContextMaxLengths = {
  companyDescription: 300,
  teamDescription: 300,
  culture: 250,
  values: 200,
  leadershipStyle: 250,
  workStyle: 250,
  expectations: 300,
  additionalContext: 300,
};

const CompanyContextTab = () => {
  const { authToken, organization, project, showAlert } = useStore.getState();
  const { t } = useTranslation();

  const [companyContext, setCompanyContext] = useState(emptyCompanyContext);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);

  const hasContent = useMemo(() => {
    return Object.values(companyContext).some((value) => value.trim() !== "");
  }, [companyContext]);

  const handleChange = (field, value) => {
    const maxLength = companyContextMaxLengths[field];

    if (maxLength && value.length > maxLength) {
      return;
    }

    setCompanyContext((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const loadCompanyContext = async () => {
    try {
      setLoadingConfig(true);

      if (!organization || !project) {
        setLoadingConfig(false);
        return;
      }

      const response = await api.get(
        "/projects/company-context",
        {
          params: {
            organizationId: organization,
            projectId: project,
          },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (response.data.response.status_response === "OK") {
        setCompanyContext({
          ...emptyCompanyContext,
          ...(response.data.response.data || {}),
        });
      }
    } catch (err) {
      console.error("Error cargando contexto de empresa y equipo:", err);
      showAlert(t("companyContextConfig.messages.loadError"), "error");
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleSave = async () => {
    try {
      setSavingConfig(true);

      if (!organization || !project) {
        showAlert(t("companyContextConfig.messages.cannotSave"), "error");
        return;
      }

      const response = await api.put(
        "/projects/company-context",
        companyContext,
        {
          params: {
            organizationId: organization,
            projectId: project,
          },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (response.data.response.status_response === "OK") {
        showAlert(t("companyContextConfig.messages.saveSuccess"), "success");
      } else {
        showAlert(t("companyContextConfig.messages.saveError"), "error");
      }
    } catch (err) {
      console.error("Error guardando contexto de empresa y equipo:", err);
      showAlert(t("companyContextConfig.messages.saveError"), "error");
    } finally {
      setSavingConfig(false);
    }
  };

  useEffect(() => {
    loadCompanyContext();
  }, [organization, project]);

  return (
    <Box>
      {loadingConfig && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>
            {t("companyContextConfig.loading")}
          </Typography>
        </Box>
      )}

      {!loadingConfig && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <BusinessIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">
                {t("companyContextConfig.title")}
              </Typography>
            </Box>

            <Typography variant="body1" color="text.secondary" paragraph>
              {t("companyContextConfig.description")}
            </Typography>

            <Alert severity="info">
              {t("companyContextConfig.info")}
            </Alert>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t("companyContextConfig.fields.companyDescription.label")}
                  value={companyContext.companyDescription}
                  onChange={(e) => handleChange("companyDescription", e.target.value)}
                  placeholder={t("companyContextConfig.fields.companyDescription.placeholder")}
                  inputProps={{ maxLength: companyContextMaxLengths.companyDescription }}
                  helperText={`${companyContext.companyDescription.length}/${companyContextMaxLengths.companyDescription}`}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t("companyContextConfig.fields.teamDescription.label")}
                  value={companyContext.teamDescription}
                  onChange={(e) => handleChange("teamDescription", e.target.value)}
                  placeholder={t("companyContextConfig.fields.teamDescription.placeholder")}
                  inputProps={{ maxLength: companyContextMaxLengths.teamDescription }}
                  helperText={`${companyContext.teamDescription.length}/${companyContextMaxLengths.teamDescription}`}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t("companyContextConfig.fields.culture.label")}
                  value={companyContext.culture}
                  onChange={(e) => handleChange("culture", e.target.value)}
                  placeholder={t("companyContextConfig.fields.culture.placeholder")}
                  inputProps={{ maxLength: companyContextMaxLengths.culture }}
                  helperText={`${companyContext.culture.length}/${companyContextMaxLengths.culture}`}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t("companyContextConfig.fields.values.label")}
                  value={companyContext.values}
                  onChange={(e) => handleChange("values", e.target.value)}
                  placeholder={t("companyContextConfig.fields.values.placeholder")}
                  inputProps={{ maxLength: companyContextMaxLengths.values }}
                  helperText={`${companyContext.values.length}/${companyContextMaxLengths.values}`}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t("companyContextConfig.fields.leadershipStyle.label")}
                  value={companyContext.leadershipStyle}
                  onChange={(e) => handleChange("leadershipStyle", e.target.value)}
                  placeholder={t("companyContextConfig.fields.leadershipStyle.placeholder")}
                  inputProps={{ maxLength: companyContextMaxLengths.leadershipStyle }}
                  helperText={`${companyContext.leadershipStyle.length}/${companyContextMaxLengths.leadershipStyle}`}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t("companyContextConfig.fields.workStyle.label")}
                  value={companyContext.workStyle}
                  onChange={(e) => handleChange("workStyle", e.target.value)}
                  placeholder={t("companyContextConfig.fields.workStyle.placeholder")}
                  inputProps={{ maxLength: companyContextMaxLengths.workStyle }}
                  helperText={`${companyContext.workStyle.length}/${companyContextMaxLengths.workStyle}`}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t("companyContextConfig.fields.expectations.label")}
                  value={companyContext.expectations}
                  onChange={(e) => handleChange("expectations", e.target.value)}
                  placeholder={t("companyContextConfig.fields.expectations.placeholder")}
                  inputProps={{ maxLength: companyContextMaxLengths.expectations }}
                  helperText={`${companyContext.expectations.length}/${companyContextMaxLengths.expectations}`}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t("companyContextConfig.fields.additionalContext.label")}
                  value={companyContext.additionalContext}
                  onChange={(e) => handleChange("additionalContext", e.target.value)}
                  placeholder={t("companyContextConfig.fields.additionalContext.placeholder")}
                  inputProps={{ maxLength: companyContextMaxLengths.additionalContext }}
                  helperText={`${companyContext.additionalContext.length}/${companyContextMaxLengths.additionalContext}`}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={savingConfig || !hasContent}
                startIcon={savingConfig ? <CircularProgress size={20} /> : <SaveIcon />}
                size="large"
              >
                {savingConfig ? t("saving") : t("companyContextConfig.saveButton")}
              </Button>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default CompanyContextTab;